import React from "react";
import { ExternalLink, Trash2 } from "lucide-react";
import { EmptyList } from "../../../components/empty-list";
import { useSiteProgress, useRemoveCreatedSite } from "../hooks";
import { CreatedSiteStatus } from "../../../__generated__/globalTypes";
import { createdSiteStatusLabel } from "../../../helpers/enum-labels";
import { formatDisplayDate } from "../../../helpers/date";

interface ICreatedSiteRow {
  id: number;
  url: string;
  status: CreatedSiteStatus;
  creationPercentage: number;
  createdAt: string;
  triggeredBy?: {
    firstname: string;
    lastname: string;
  } | null;
}

const STATUS_STYLES: Record<CreatedSiteStatus, string> = {
  [CreatedSiteStatus.Creating]: "bg-amber-100 text-amber-700",
  [CreatedSiteStatus.Done]: "bg-green-100 text-green-700",
  [CreatedSiteStatus.Error]: "bg-red-100 text-red-700",
};

const CreatedSiteRow: React.FC<{ site: ICreatedSiteRow; isAdmin?: boolean }> = ({
  site,
  isAdmin,
}) => {
  const isCreating = site.status === CreatedSiteStatus.Creating;
  const { data: progressData } = useSiteProgress({ createdSiteId: site.id }, !isCreating);
  const { removeCreatedSite, loading: isRemoving } = useRemoveCreatedSite();

  const creationPercentage =
    progressData?.siteProgressUpdated?.creationPercentage ?? site.creationPercentage;
  const hermesDiagnostic = progressData?.siteProgressUpdated?.hermesDiagnostic;

  const siteUrl = site.url
    ? site.url.startsWith("http")
      ? site.url
      : `https://${site.url}`
    : null;

  const handleRemove = () => {
    if (
      !window.confirm(
        "Supprimer ce site généré ? Cette action est irréversible."
      )
    ) {
      return;
    }
    removeCreatedSite(site.id);
  };

  return (
    <div className="border border-gray-200 dark:border-zinc-800 rounded-md p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-gray-500 dark:text-zinc-400">
          {formatDisplayDate(site.createdAt)}
          {site.triggeredBy && (
            <>
              {" · "}
              Généré par {site.triggeredBy.firstname} {site.triggeredBy.lastname}
            </>
          )}
        </span>
        <div className="flex items-center gap-2">
          <span
            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[site.status]}`}
          >
            {createdSiteStatusLabel(site.status)}
          </span>
          {isAdmin && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={isRemoving}
              title="Supprimer ce site"
              className="text-gray-400 hover:text-red-600 disabled:opacity-50"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {isCreating && (
        <div className="mt-2">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-zinc-400 mb-1">
            <span>{hermesDiagnostic ?? "Génération en cours..."}</span>
            <span>{creationPercentage}%</span>
          </div>
          <div className="w-full h-2 bg-gray-100 dark:bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-600 rounded-full transition-all duration-500"
              style={{ width: `${creationPercentage}%` }}
            />
          </div>
        </div>
      )}

      {site.status === CreatedSiteStatus.Done && siteUrl && (
        <a
          href={siteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-2 text-sm text-indigo-600 hover:underline"
        >
          <ExternalLink size={13} />
          {site.url}
        </a>
      )}
    </div>
  );
};

interface ICreatedSitesHistory {
  sites: ICreatedSiteRow[];
  isAdmin?: boolean;
}

export const CreatedSitesHistory: React.FC<ICreatedSitesHistory> = ({ sites, isAdmin }) => {
  if (!sites.length) {
    return <EmptyList text="Aucune génération de site" />;
  }

  const sorted = [...sites].sort((a, b) => b.id - a.id);

  return (
    <div className="space-y-3">
      {sorted.map((site) => (
        <CreatedSiteRow key={`created-site-${site.id}`} site={site} isAdmin={isAdmin} />
      ))}
    </div>
  );
};
